(function () {
  if (window.__VOESH_AUTO_FREE_GIFT_LOADED__) return;
  window.__VOESH_AUTO_FREE_GIFT_LOADED__ = true;

  const CONFIG = {
    cartRequestDelay: 150,
    maxSyncPasses: 5,
    debug: false
  };

  const GWP = {
    running: false,
    queued: false,
    isRefreshingCartUI: false,
    debounceTimer: null,
    originalFetch: null,
    originalXHROpen: null,
    originalXHRSend: null,

    log(...args) {
      if (CONFIG.debug) console.log('[VOESH GWP]', ...args);
    },

    warn(...args) {
      console.warn('[VOESH GWP]', ...args);
    },

    error(...args) {
      console.error('[VOESH GWP]', ...args);
    },

    getRules() {
      let rules = [];

      try {
        const jsonEl = document.querySelector('[data-voesh-auto-free-gifts-json]');

        if (jsonEl && jsonEl.textContent.trim()) {
          rules = JSON.parse(jsonEl.textContent.trim());
        } else if (Array.isArray(window.VOESH_AUTO_FREE_GIFTS)) {
          rules = window.VOESH_AUTO_FREE_GIFTS;
        }
      } catch (err) {
        this.error('Invalid free gift JSON.', err);
        rules = [];
      }

      if (!Array.isArray(rules)) return [];

      return rules
        .filter(Boolean)
        .filter((rule) => rule.enabled !== false)
        .filter((rule) => rule.giftVariantId)
        .map((rule, index) => {
          return {
            ...rule,
            ruleIndex: index,
            ruleId: String(rule.ruleId || rule.giftVariantId),
            title: String(rule.title || 'Automatic Free Gift'),
            giftVariantId: Number(rule.giftVariantId),
            giftVariantSku: String(rule.giftVariantSku || '').trim(),
            giftQuantity: Math.max(1, Number(rule.giftQuantity || 1)),
            giftAvailable: rule.giftAvailable !== false,
            triggerSkus: this.normalizeSkuList(rule.triggerSkus),
            minSpend: this.normalizeMoneyValue(rule.minSpend),
            maxSpend: this.normalizeMoneyValue(rule.maxSpend),
            bogo: Boolean(rule.bogo)
          };
        })
        .filter((rule) => rule.giftVariantSku);
    },

    normalizeSkuList(value) {
      if (!value) return [];

      if (Array.isArray(value)) {
        return value
          .map((item) => String(item || '').trim())
          .filter(Boolean);
      }

      return String(value)
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
    },

    normalizeMoneyValue(value) {
      if (value === null || value === undefined || value === '') return null;

      const number = Number(String(value).replace(/[^0-9.]/g, ''));

      if (!Number.isFinite(number) || number <= 0) return null;

      return Math.round(number);
    },

    isCartUrl(url) {
      const value = String(url || '');

      return (
        value.includes('/cart/add') ||
        value.includes('/cart/change') ||
        value.includes('/cart/update') ||
        value.includes('/cart/clear')
      );
    },

    async getCart() {
      const response = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Unable to fetch cart.');
      }

      return response.json();
    },

    async addGift(rule, quantity) {
      this.log('Adding gift', rule.title, rule.giftVariantSku, rule.giftVariantId, quantity);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: rule.giftVariantId,
          quantity: Number(quantity || 1),
          properties: {
            _auto_free_gift: true,
            _freeGift: true,
            _min_spend: rule.minSpend || 0,
            _index: rule.ruleIndex || 0,

            _voesh_auto_free_gift: 'true',
            _voesh_auto_free_gift_rule_id: String(rule.ruleId),
            _voesh_auto_free_gift_title: String(rule.title),
            _voesh_auto_free_gift_sku: String(rule.giftVariantSku)
          }
        })
      });

      if (!response.ok) {
        const text = await response.text().catch(() => '');
        this.warn('Could not add gift.', rule, text);
        return false;
      }

      return true;
    },

    async changeLine(lineKey, quantity) {
      this.log('Changing line', lineKey, quantity);

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: Number(quantity)
        })
      });

      if (!response.ok) {
        const text = await response.text().catch(() => '');
        this.warn('Could not change cart line.', lineKey, quantity, text);
        return false;
      }

      return true;
    },

    hasTruthyProperty(properties, key) {
      if (!properties) return false;

      return (
        properties[key] === true ||
        properties[key] === 'true' ||
        properties[key] === 'True' ||
        properties[key] === 1 ||
        properties[key] === '1'
      );
    },

    isManagedGiftLine(item) {
      if (!item || !item.properties) return false;

      return (
        this.hasTruthyProperty(item.properties, '_voesh_auto_free_gift') ||
        this.hasTruthyProperty(item.properties, '_auto_free_gift') ||
        this.hasTruthyProperty(item.properties, '_freeGift')
      );
    },

    isManagedGiftLineForRule(item, rule) {
      if (!item) return false;

      const sameVariant =
        Number(item.variant_id || item.id) === Number(rule.giftVariantId);

      if (!sameVariant) return false;

      const props = item.properties || {};

      const sameVoeshRule =
        props._voesh_auto_free_gift_rule_id !== undefined &&
        String(props._voesh_auto_free_gift_rule_id) === String(rule.ruleId);

      const sameLegacyIndex =
        props._index !== undefined &&
        String(props._index) === String(rule.ruleIndex || 0);

      const hasAutoGiftProp =
        this.hasTruthyProperty(props, '_voesh_auto_free_gift') ||
        this.hasTruthyProperty(props, '_auto_free_gift') ||
        this.hasTruthyProperty(props, '_freeGift');

      return sameVariant && (sameVoeshRule || sameLegacyIndex || hasAutoGiftProp);
    },

    getGiftLinesForRule(cart, rule) {
      return cart.items.filter((item) => this.isManagedGiftLineForRule(item, rule));
    },

    getQualifyingSubtotal(cart) {
      return cart.items.reduce((subtotal, item) => {
        const linePrice =
          Number(item.original_line_price) ||
          Number(item.final_line_price) ||
          Number(item.line_price) ||
          0;

        const isGiftForAnyRule = this.getRules().some((rule) => {
          return this.isManagedGiftLineForRule(item, rule);
        });

        if (isGiftForAnyRule) return subtotal;

        return subtotal + linePrice;
      }, 0);
    },

    itemMatchesTrigger(item, rule) {
      if (!item) return false;

      const sku = String(item.sku || '').trim();

      if (!sku) return false;

      const isGiftVariant =
        Number(item.variant_id || item.id) === Number(rule.giftVariantId);

      const isGiftLineForThisRule = this.isManagedGiftLineForRule(item, rule);

      // Important edge case:
      // Trigger product and gift product can be the same product, but different variants/SKUs.
      // Only exclude the actual managed gift line, not the entire product.
      if (isGiftVariant && isGiftLineForThisRule) return false;

      return rule.triggerSkus.includes(sku);
    },

    getTriggerItems(cart, rule) {
      return cart.items.filter((item) => this.itemMatchesTrigger(item, rule));
    },

    getTriggerQuantity(cart, rule) {
      if (!rule.triggerSkus.length) {
        return 1;
      }

      return this.getTriggerItems(cart, rule).reduce((total, item) => {
        return total + Number(item.quantity || 0);
      }, 0);
    },

    cartMeetsSpend(cart, rule) {
      const qualifyingSubtotal = this.getQualifyingSubtotal(cart);

      if (rule.minSpend !== null && qualifyingSubtotal < rule.minSpend) {
        return false;
      }

      if (rule.maxSpend !== null && qualifyingSubtotal > rule.maxSpend) {
        return false;
      }

      return true;
    },

    shouldHaveGift(cart, rule) {
      if (!rule.giftAvailable) return false;

      const ruleUsesTriggerSkus = rule.triggerSkus.length > 0;
      let triggerRequirementMet = true;

      if (ruleUsesTriggerSkus) {
        triggerRequirementMet = this.getTriggerItems(cart, rule).length > 0;
      }

      const spendRequirementMet = this.cartMeetsSpend(cart, rule);

      return triggerRequirementMet && spendRequirementMet;
    },

    getDesiredGiftQuantity(cart, rule) {
      if (rule.bogo) {
        return Math.max(1, this.getTriggerQuantity(cart, rule));
      }

      return Math.max(1, Number(rule.giftQuantity || 1));
    },

    async cleanDuplicateGiftLines(cart, rule) {
      const giftLines = this.getGiftLinesForRule(cart, rule);

      if (giftLines.length <= 1) return false;

      const duplicateLines = giftLines.slice(1);
      let changed = false;

      for (const duplicateLine of duplicateLines) {
        const removed = await this.changeLine(duplicateLine.key, 0);
        if (removed) changed = true;
      }

      return changed;
    },

    async syncRule(cart, rule) {
      const duplicateCleanupChanged = await this.cleanDuplicateGiftLines(cart, rule);

      if (duplicateCleanupChanged) {
        return true;
      }

      const giftLines = this.getGiftLinesForRule(cart, rule);
      const giftLine = giftLines[0];
      const shouldHaveGift = this.shouldHaveGift(cart, rule);
      const desiredQuantity = this.getDesiredGiftQuantity(cart, rule);

      if (shouldHaveGift && !giftLine) {
        return await this.addGift(rule, desiredQuantity);
      }

      if (!shouldHaveGift && giftLine) {
        return await this.changeLine(giftLine.key, 0);
      }

      if (
        shouldHaveGift &&
        giftLine &&
        Number(giftLine.quantity) !== Number(desiredQuantity)
      ) {
        return await this.changeLine(giftLine.key, desiredQuantity);
      }

      return false;
    },

    async sync() {
      if (this.running) {
        this.queued = true;
        return;
      }

      const rules = this.getRules();

      if (!rules.length) return;

      this.running = true;
      this.queued = false;

      let anyChange = false;

      try {
        let cart = await this.getCart();

        for (const rule of rules) {
          const changedRule = await this.syncRule(cart, rule);

          if (changedRule) {
            anyChange = true;

            // Get the new cart only after an actual add/remove/update.
            // This prevents stale line indexes and duplicate gift logic.
            cart = await this.getCart();
          }
        }

        if (anyChange) {
          this.refreshCartUI();
        }
      } catch (err) {
        this.error('Sync failed.', err);
      } finally {
        this.running = false;

        if (this.queued) {
          this.queued = false;
          this.queueSync(100);
        }
      }
    },

    queueSync(delay) {
      clearTimeout(this.debounceTimer);

      this.debounceTimer = setTimeout(() => {
        this.sync();
      }, typeof delay === 'number' ? delay : CONFIG.cartRequestDelay);
    },

    refreshCartUI() {
      this.isRefreshingCartUI = true;

      document.dispatchEvent(new CustomEvent('voesh:auto-free-gift:updated'));

      // Current cart.js listens for this and re-renders main-cart.
      document.dispatchEvent(new CustomEvent('ajaxProduct:added'));

      setTimeout(() => {
        this.isRefreshingCartUI = false;
      }, 700);

      if (window.location.pathname === '/cart') {
        setTimeout(() => {
          window.location.reload();
        }, 250);
      }
    },

    watchFetchCartRequests() {
      if (this.originalFetch) return;
      if (!window.fetch) return;

      this.originalFetch = window.fetch;

      const self = this;

      window.fetch = async function (...args) {
        const response = await self.originalFetch.apply(this, args);

        try {
          const rawUrl = typeof args[0] === 'string' ? args[0] : args[0] && args[0].url;
          const url = String(rawUrl || '');

          if (self.isCartUrl(url)) {
            self.queueSync();
          }
        } catch (err) {
          self.warn('Fetch watcher failed.', err);
        }

        return response;
      };
    },

    watchXHRCartRequests() {
      if (this.originalXHROpen || this.originalXHRSend) return;
      if (!window.XMLHttpRequest) return;

      const self = this;

      this.originalXHROpen = window.XMLHttpRequest.prototype.open;
      this.originalXHRSend = window.XMLHttpRequest.prototype.send;

      window.XMLHttpRequest.prototype.open = function (method, url) {
        this.__voeshGwpUrl = url;
        return self.originalXHROpen.apply(this, arguments);
      };

      window.XMLHttpRequest.prototype.send = function () {
        this.addEventListener('loadend', function () {
          if (self.isCartUrl(this.__voeshGwpUrl)) {
            self.queueSync();
          }
        });

        return self.originalXHRSend.apply(this, arguments);
      };
    },

    watchCartEvents() {
      const events = [
        'ajax-cart:changed',
        'cart:update',
        'cart:updated',
        'cart:refresh'
      ];

      events.forEach((eventName) => {
        document.addEventListener(eventName, () => {
          if (this.isRefreshingCartUI) return;
          this.queueSync();
        });
      });
    },

    init() {
      this.watchFetchCartRequests();
      this.watchXHRCartRequests();
      this.watchCartEvents();

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          this.sync();
        });
      } else {
        this.sync();
      }
    }
  };

  window.VOESH_AUTO_FREE_GIFT = GWP;

  GWP.init();

  debug: Boolean(window.VOESH_AUTO_FREE_GIFT_DEBUG);
})();